import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus,
  RefreshCcw,
  Search,
  Pencil,
  X,
  Loader2,
  Trash2,
  Tag,
  Percent,
  BadgeInfo,
  UserPlus,
  Users,
  TicketPercent,
  Smartphone,
  CheckCircle2,
  Clock,
} from "lucide-react";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../Inventory/Inventory/InventoryAccordion.css";
import MyContext from "../../../MyContext";
import "../Udhari/CreditStyle.css";
import loader from "../../../images/Anlook.svg";

/** Endpoints */
const API = {
  LIST: "/api/Offer/v1.0/getSpecialOffer",
  ADD: "/api/Offer/v1.0/insertSpecialOffer",
  ASSIGN: "/api/Offer/v1.0/insertCustomerSpecialOffer",
  DEL: "/api/Offer/v1.0/deleteSpecialOffer",
  CUSTOMER_LIST: "/api/Offer/v1.0/getCustomerSpecialOffer",
};

const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";

/** Helpers */
const val = (...candidates) => {
  for (const c of candidates) {
    if (c !== undefined && c !== null && c !== "") return c;
  }
  return undefined;
};

const fmtDate = (s) => (s ? String(s) : "—");

// single_use can be "Y", "yes", "Yes", "1", etc
const isYes = (v) => {
  const s = String(v ?? "").trim().toUpperCase();
  return s === "Y" || s === "YES" || s === "TRUE" || s === "1";
};

export default function SpecialOffers({ userID }) {
  const { baseUrl } = useContext(MyContext);

  // MAIN OFFERS TAB
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // CUSTOMER COUPONS TAB
  const [customerRows, setCustomerRows] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerMobile, setCustomerMobile] = useState(""); // 10-digit input

  // TABS
  const [activeTab, setActiveTab] = useState("offers"); // 'offers' | 'customer'

  // DRAWER: OFFER ADD/EDIT
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add"); // add | edit
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    couponCode: "",
    discount: "",
    discountType: "fixed", // fixed | percentage
    minimumAmount: "",
    maximumDiscount: "",
    offerTitle: "",
    offerDescription: "",
    singleUse: true, // bool; send as Y/N
  });

  // ASSIGN-TO-CUSTOMER DRAWER
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigningOffer, setAssigningOffer] = useState(null);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignForm, setAssignForm] = useState({
    mobileNumber: "", // 10-digit input
  });

  const call = (path, payload) =>
    axios.post(`${baseUrl}${path}`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${BASIC_AUTH}`,
      },
    });

  /** Load offers list */
  async function fetchOffers() {
    if (!userID) {
      toast.error("Missing userid");
      return;
    }
    try {
      setLoading(true);
      const res = await call(API.LIST, { userid: userID });

      if (res?.data?.responseCode === "00") {
        setRows(Array.isArray(res.data.response) ? res.data.response : []);
      } else {
        setRows([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("API error while fetching special offers");
    } finally {
      setLoading(false);
    }
  }

  /** Load customer coupons list by mobile (empty => all) */
  async function fetchCustomerOffers() {
    if (!userID) {
      toast.error("Missing userid");
      return;
    }

    try {
      setCustomerLoading(true);
      const trimmed = customerMobile.trim();

      // input is 10-digit – add 91 automatically, unless empty
      const payloadMobile =
        trimmed.length === 10 ? `${trimmed}` : trimmed === "" ? "" : `91${trimmed}`;

      const body = {
        userid: userID,
        mobileNumber: payloadMobile,
      };

      const res = await call(API.CUSTOMER_LIST, body);

      if (res?.data?.responseCode === "00") {
        setCustomerRows(
          Array.isArray(res.data.response) ? res.data.response : []
        );
      } else {
        setCustomerRows([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("API error while fetching customer coupons");
    } finally {
      setCustomerLoading(false);
    }
  }

  /** Save (add / edit) offer */
  async function saveOffer() {
    if (!userID) return toast.error("Missing userid");

    const err = validateForm();
    if (err) return toast.warning(err);

    try {
      setSubmitting(true);

      const body = {
        userid: userID,
        couponCode: form.couponCode.trim(),
        discount: String(form.discount || "0").trim(),
        discountType: form.discountType,
        minimumAmount: String(form.minimumAmount || "0").trim(),
        maximumDiscount: String(form.maximumDiscount || "0").trim(),
        offerTitle: form.offerTitle.trim(),
        offerDescription: form.offerDescription.trim(),
        singleUse: form.singleUse ? "Y" : "N",
      };

      // If editing, send offer id as well (assuming backend treats as update)
      if (mode === "edit" && editing) {
        const id = val(
          editing.offerid,
          editing.id,
          editing.pk_offer_id,
          editing.code,
          editing.pk_coupon_id
        );
        if (id) body.offerid = String(id);
      }

      const res = await call(API.ADD, body);

      if (res?.data?.responseCode === "00") {
        await Swal.fire({
          icon: "success",
          title: "Saved",
          text: res?.data?.responseMessage || "Special offer saved successfully",
          timer: 1300,
          showConfirmButton: false,
        });
        setOpen(false);
        setEditing(null);
        fetchOffers();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: res?.data?.responseMessage || "Failed to save special offer",
        });
      }
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: "error",
        title: "API Error",
        text: "Something went wrong while saving special offer",
      });
    } finally {
      setSubmitting(false);
    }
  }

  /** Delete offer with Swal confirmation */
  async function confirmDelete(row) {
    const id = val(
      row.offerid,
      row.id,
      row.pk_offer_id,
      row.code,
      row.pk_coupon_id
    );
    if (!id) return toast.error("Missing offer id");

    const result = await Swal.fire({
      title: "Delete this special offer?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e11d48",
      cancelButtonColor: "#4b5563",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setSubmitting(true);
      const res = await call(API.DEL, { id: String(id) });

      if (res?.data?.responseCode === "00") {
        await Swal.fire({
          icon: "success",
          title: "Deleted",
          text: res?.data?.responseMessage || "Special offer deleted",
          timer: 1200,
          showConfirmButton: false,
        });
        fetchOffers();
      } else {
        Swal.fire({
          icon: "error",
          title: "Delete failed",
          text: res?.data?.responseMessage || "Unable to delete this special offer",
        });
      }
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: "error",
        title: "API Error",
        text: "Something went wrong while deleting special offer",
      });
    } finally {
      setSubmitting(false);
    }
  }

  /** Assign offer to customer (insertCustomerSpecialOffer) */
  async function saveAssign() {
    if (!userID) return toast.error("Missing userid");
    const mobile = assignForm.mobileNumber.trim();

    if (!mobile) return toast.warning("Customer mobile is required");
    if (!/^[6-9]\d{9}$/.test(mobile))
      return toast.warning("Enter valid 10-digit mobile number");

    // ✅ Add 91 automatically in payload
    const formattedMobile = mobile.startsWith("91")
      ? mobile
      : `91${mobile}`;

    const offerId = val(
      assigningOffer?.offerid,
      assigningOffer?.id,
      assigningOffer?.pk_offer_id,
      assigningOffer?.code,
      assigningOffer?.pk_coupon_id
    );
    if (!offerId) return toast.error("Missing offer id for assignment");

    try {
      setAssignSubmitting(true);
      const body = {
        userid: userID,
        offerid: String(offerId),
        mobileNumber: formattedMobile,
      };
      const res = await call(API.ASSIGN, body);

      if (res?.data?.responseCode === "00") {
        await Swal.fire({
          icon: "success",
          title: "Assigned",
          text: res?.data?.responseMessage || "Offer assigned to customer",
          timer: 1300,
          showConfirmButton: false,
        });
        setAssignOpen(false);
        setAssigningOffer(null);
        setAssignForm({ mobileNumber: "" });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: res?.data?.responseMessage || "Failed to assign offer",
        });
      }
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: "error",
        title: "API Error",
        text: "Something went wrong while assigning offer",
      });
    } finally {
      setAssignSubmitting(false);
    }
  }

  /** Effects */

  // Always load master offers initially
  useEffect(() => {
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, baseUrl]);

  // Whenever tab switches to "customer", auto fetch list (current search mobile)
  useEffect(() => {
    if (activeTab === "customer") {
      fetchCustomerOffers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userID, baseUrl]);

  /** Search filter (master offers) */
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((r) =>
      [
        r.couponCode,
        r.coupon_code,
        r.offerTitle,
        r.offer_title,
        r.offer_name,
        r.name,
        r.offerDescription,
        r.description,
      ]
        .map((v) => String(v || "").toLowerCase())
        .some((s) => s.includes(needle))
    );
  }, [rows, q]);

  /** Drawer helpers */
  function openAdd() {
    setMode("add");
    setEditing(null);
    setForm({
      couponCode: "",
      discount: "",
      discountType: "fixed",
      minimumAmount: "",
      maximumDiscount: "",
      offerTitle: "",
      offerDescription: "",
      singleUse: true,
    });
    setOpen(true);
  }

  function openEdit(row) {
    setMode("edit");
    setEditing(row || null);

    const couponCode = val(
      row.couponCode,
      row.coupon_code,
      row.tag_code,
      row.code,
      row.coupon_title,
      ""
    );
    const discount = val(row.discount, row.discount_value, "0");
    const discountTypeRaw = val(
      row.discountType,
      row.discount_type,
      Number(discount) > 0 && Number(row.maximumDiscount || 0) > 0
        ? "percentage"
        : "fixed"
    );
    const discountType = String(discountTypeRaw || "")
      .toLowerCase()
      .startsWith("p")
      ? "percentage"
      : "fixed";

    const minimumAmount = val(row.minimumAmount, row.min_amount, "");
    const maximumDiscount = val(row.maximumDiscount, row.max_discount, "");
    const offerTitle = val(
      row.offerTitle,
      row.offer_title,
      row.offer_name,
      row.name,
      couponCode
    );
    const offerDescription = val(
      row.offerDescription,
      row.description,
      ""
    );
    const singleUse = isYes(
      val(row.singleUse, row.single_use, row.single_use_flag, "N")
    );

    setForm({
      couponCode: couponCode || "",
      discount: discount || "",
      discountType: discountType || "fixed",
      minimumAmount: minimumAmount || "",
      maximumDiscount: maximumDiscount || "",
      offerTitle: offerTitle || "",
      offerDescription: offerDescription || "",
      singleUse,
    });

    setOpen(true);
  }

  function closeDrawer() {
    if (!submitting) {
      setOpen(false);
      setEditing(null);
    }
  }

  function openAssign(row) {
    setAssigningOffer(row);
    setAssignForm({ mobileNumber: "" });
    setAssignOpen(true);
  }

  function closeAssignDrawer() {
    if (!assignSubmitting) {
      setAssignOpen(false);
      setAssigningOffer(null);
      setAssignForm({ mobileNumber: "" });
    }
  }

  /** Validation */
  function validateForm() {
    if (!form.couponCode.trim()) return "Coupon code is required";
    if (!form.offerTitle.trim()) return "Offer title is required";
    if (!form.discount.toString().trim()) return "Discount value is required";

    const discountNum = Number(form.discount);
    if (Number.isNaN(discountNum) || discountNum <= 0)
      return "Enter valid discount value";

    if (form.minimumAmount && Number.isNaN(Number(form.minimumAmount))) {
      return "Minimum amount must be a number";
    }
    if (form.maximumDiscount && Number.isNaN(Number(form.maximumDiscount))) {
      return "Maximum discount must be a number";
    }

    return null;
  }

  /** RENDER */
return (
  <div className="inv-industrial-wrap">
    <ToastContainer position="top-right" theme="colored" closeOnClick pauseOnHover={false} />

    {/* Header (Inventory-style) */}
    <div className="inv-industrial-head">
      <div className="inv-industrial-title">
        <h1 className="CreateCampaign">Special Offers & Coupons</h1>
        {/* <div className="inv-industrial-sub">
          Offer Master: <span className="inv-mono">{API.LIST}</span> &nbsp;|&nbsp; Customer Coupons:{" "}
          <span className="inv-mono">{API.CUSTOMER_LIST}</span>
        </div> */}
      </div>

      <div className="inv-industrial-actions">
        {activeTab === "offers" ? (
          <>
            <button
              type="button"
              className="inv-btn inv-btn-primary"
              onClick={openAdd}
              disabled={submitting || loading}
            >
              <Plus size={16} />
              New Offer
            </button>
            <button
              type="button"
              className="inv-btn inv-btn-ghost"
              onClick={fetchOffers}
              disabled={loading}
              title="Refresh"
            >
              <RefreshCcw size={16} />
            </button>
          </>
        ) : (
          <button
            type="button"
            className="inv-btn inv-btn-ghost"
            onClick={fetchCustomerOffers}
            disabled={customerLoading}
            title="Refresh"
          >
            <RefreshCcw size={16} />
          </button>
        )}
      </div>
    </div>

    {/* Tabs (Inventory-style) */}
    <div className="inv-tabs">
      <button
        type="button"
        className={`inv-tab ${activeTab === "offers" ? "active" : ""}`}
        onClick={() => setActiveTab("offers")}
      >
        <TicketPercent size={16} />
        Offer Master
      </button>
      <button
        type="button"
        className={`inv-tab ${activeTab === "customer" ? "active" : ""}`}
        onClick={() => setActiveTab("customer")}
      >
        <Users size={16} />
        Customer Coupons
      </button>
    </div>

    {/* Toolbar (Inventory-style) */}
    {activeTab === "offers" && (
      <div className="inv-toolbar-row">
        <div className="inv-industrial-search">
          <Search size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by coupon code, offer title or description…"
          />
        </div>

        <div className="inv-toolbar-meta">
          <span className="inv-pill">Total: {rows.length}</span>
          {loading ? <span className="inv-pill muted">Loading…</span> : null}
        </div>
      </div>
    )}

    {activeTab === "customer" && (
      <div className="inv-toolbar-row">
        <div className="inv-industrial-search">
          <Smartphone size={16} />
          <input
            value={customerMobile}
            onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="Enter 10-digit customer mobile (blank = all)…"
          />
        </div>

        <div className="inv-toolbar-meta" style={{ gap: 8 }}>
          <button
            type="button"
            className="inv-btn inv-btn-primary"
            onClick={fetchCustomerOffers}
            disabled={customerLoading}
          >
            <Search size={16} />
            Search
          </button>

          <button
            type="button"
            className="inv-btn"
            onClick={() => {
              setCustomerMobile("");
              fetchCustomerOffers();
            }}
            disabled={customerLoading}
          >
            <X size={16} />
            Clear
          </button>

          <span className="inv-pill">Total: {customerRows.length}</span>
          {customerLoading ? <span className="inv-pill muted">Loading…</span> : null}
        </div>
      </div>
    )}

    {/* CONTENT (Inventory industrial grid tables) */}
    <div className="inv-industrial-card">
      <div className="inv-industrial-table">
        {/* OFFER MASTER TABLE */}
        {activeTab === "offers" && (
          <>
            <div
              className="inv-industrial-row inv-industrial-headrow"
              style={{ gridTemplateColumns: "160px 1.5fr 140px 130px 140px 120px 160px 180px" }}
            >
              <div>Coupon</div>
              <div>Offer</div>
              <div>Discount</div>
              <div>Min Amount</div>
              <div>Max Discount</div>
              <div>Single Use</div>
              <div>Created On</div>
              <div className="inv-industrial-right">Actions</div>
            </div>

            {loading ? (
              <div className="inv-industrial-empty">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="inv-industrial-empty">No special offers found.</div>
            ) : (
              filtered.map((r) => {
                const id = val(r.offerid, r.id, r.pk_offer_id, r.code, r.pk_coupon_id);

                const couponCode = val(
                  r.couponCode,
                  r.coupon_code,
                  r.tag_code,
                  r.code,
                  r.coupon_title,
                  "—"
                );

                const title = val(
                  r.offerTitle,
                  r.offer_title,
                  r.offer_name,
                  r.name,
                  couponCode
                );

                const desc = val(r.offerDescription, r.description, "");

                const discount = val(r.discount, r.discount_value, "0");
                const discountTypeRaw = val(r.discountType, r.discount_type, "fixed");
                const discountType = String(discountTypeRaw || "").toLowerCase().startsWith("p")
                  ? "percentage"
                  : "fixed";

                const discountLabel = discountType === "percentage" ? `${discount}%` : `₹${discount}`;

                const minAmount = val(r.minimumAmount, r.min_amount, "0");
                const maxDiscount = val(r.maximumDiscount, r.max_discount, "");
                const maxDiscountLabel =
                  maxDiscount && Number(maxDiscount) > 0 ? `₹${maxDiscount}` : "—";

                const singleUseFlag = isYes(val(r.singleUse, r.single_use, r.single_use_flag, "N"));

                const created = val(r.created_on, r.createdOn, r.createdDate, "—");

                return (
                  <div
                    key={id || couponCode}
                    className="inv-industrial-row"
                    style={{ gridTemplateColumns: "160px 1.5fr 140px 130px 140px 120px 160px 180px" }}
                  >
                    <div className="inv-mono" title={couponCode}>
                      <Tag size={14} style={{ marginRight: 6, opacity: 0.7 }} />
                      {couponCode}
                    </div>

                    <div className="inv-industrial-name">
                      <div className="inv-industrial-name-main">{title}</div>
                      <div className="inv-industrial-name-sub" title={desc}>
                        {desc || "—"}
                      </div>
                    </div>

                    <div>
                      <span className="inv-badge-industrial slate">
                        <Percent size={14} style={{ marginRight: 6 }} />
                        {discountLabel}
                      </span>
                    </div>

                    <div className="inv-mono">₹{minAmount || "0"}</div>

                    <div className="inv-mono">{maxDiscountLabel}</div>

                    <div>
                      {singleUseFlag ? (
                        <span className="inv-badge-industrial green">Yes</span>
                      ) : (
                        <span className="inv-badge-industrial slate">No</span>
                      )}
                    </div>

                    <div className="inv-mono">{fmtDate(created)}</div>

                    <div className="inv-industrial-actions-cell">
                      <button
                        type="button"
                        className="inv-iconbtn"
                        title="Assign to customer"
                        onClick={() => openAssign(r)}
                        disabled={assignSubmitting}
                      >
                        <UserPlus size={16} />
                      </button>

                      <button
                        type="button"
                        className="inv-iconbtn primary"
                        title="Edit offer"
                        onClick={() => openEdit(r)}
                      >
                        <Pencil size={16} />
                      </button>

                      <button
                        type="button"
                        className="inv-iconbtn"
                        title="Delete offer"
                        onClick={() => confirmDelete(r)}
                        disabled={submitting}
                        style={{
                          borderColor: "rgba(239,68,68,0.25)",
                          color: "#991b1b",
                          background:
                            "linear-gradient(135deg, rgba(239,68,68,0.10), rgba(239,68,68,0.04))",
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}

        {/* CUSTOMER COUPONS TABLE */}
        {activeTab === "customer" && (
          <>
            <div
              className="inv-industrial-row inv-industrial-headrow"
              style={{ gridTemplateColumns: "160px 170px 140px 130px 140px 120px 150px 120px" }}
            >
              <div>Coupon</div>
              <div>Customer Mobile</div>
              <div>Discount</div>
              <div>Min Amount</div>
              <div>Max Discount</div>
              <div>Single Use</div>
              <div>Order Type</div>
              <div>Status</div>
            </div>

            {customerLoading ? (
              <div className="inv-industrial-empty">Loading…</div>
            ) : customerRows.length === 0 ? (
              <div className="inv-industrial-empty">No customer coupons found.</div>
            ) : (
              customerRows.map((r) => {
                const id = val(r.pk_coupon_id, r.offerid, r.id, r.pk_offer_id);

                const couponCode = val(r.coupon_code, r.couponCode, "—");
                const mobile = r.customer_mobile || "Not linked";

                const discount = val(r.discount, "0");
                const discountTypeRaw = val(r.discount_type, r.discountType, "Fixed");
                const discountType = String(discountTypeRaw || "").toLowerCase().startsWith("p")
                  ? "percentage"
                  : "fixed";
                const discountLabel = discountType === "percentage" ? `${discount}%` : `₹${discount}`;

                const minAmount = val(r.min_amount, "0");
                const maxDiscount = val(r.max_discount, "");
                const maxDiscountLabel =
                  maxDiscount && Number(maxDiscount) > 0 ? `₹${maxDiscount}` : "—";

                const singleUseFlag = isYes(val(r.single_use, r.singleUse, "N"));
                const orderType = val(r.order_type, "—");

                const status = val(r.coupon_status, "—");
                const statusActive = String(status).toUpperCase() === "Y";

                return (
                  <div
                    key={id || couponCode + mobile}
                    className="inv-industrial-row"
                    style={{ gridTemplateColumns: "160px 170px 140px 130px 140px 120px 150px 120px" }}
                  >
                    <div className="inv-mono" title={couponCode}>
                      <Tag size={14} style={{ marginRight: 6, opacity: 0.7 }} />
                      {couponCode}
                    </div>

                    <div className="inv-mono">
                      {mobile === "Not linked" ? "—" : `+91 ${mobile}`}
                    </div>

                    <div>
                      <span className="inv-badge-industrial slate">
                        <Percent size={14} style={{ marginRight: 6 }} />
                        {discountLabel}
                      </span>
                    </div>

                    <div className="inv-mono">₹{minAmount || "0"}</div>

                    <div className="inv-mono">{maxDiscountLabel}</div>

                    <div>
                      {singleUseFlag ? (
                        <span className="inv-badge-industrial green">Yes</span>
                      ) : (
                        <span className="inv-badge-industrial slate">No</span>
                      )}
                    </div>

                    <div>
                      <span className="inv-badge-industrial slate">
                        {orderType === "home_delivery" ? "Home Delivery" : orderType || "—"}
                      </span>
                    </div>

                    <div>
                      {statusActive ? (
                        <span className="inv-badge-industrial green">
                          <CheckCircle2 size={14} style={{ marginRight: 6 }} />
                          Active
                        </span>
                      ) : (
                        <span className="inv-badge-industrial red">
                          <Clock size={14} style={{ marginRight: 6 }} />
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>

       {/* ADD / EDIT OFFER DRAWER */}
      {open && (
        <div className="admin-drawer-backdrop" onClick={closeDrawer}>
          <div
            className="admin-drawer admin-drawer-compact"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-drawer-header">
              <h4 className="admin-drawer-title">
                {mode === "add" ? "Create Special Offer" : "Edit Special Offer"}
              </h4>
              <button className="admin-icon-btn" onClick={closeDrawer}>
                <X size={18} />
              </button>
            </div>

            <form
              className="admin-drawer-body admin-drawer-form admin-drawer-scroll"
              onSubmit={(e) => {
                e.preventDefault();
                if (!submitting) saveOffer();
              }}
            >
              {/* Row 1: Coupon + Single Use */}
              <div className="admin-field-grid">
                <div className="admin-field">
                  <label>
                    Coupon Code <span className="req">*</span>
                  </label>
                  <input
                    className="admin-input-control mono"
                    value={form.couponCode}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        couponCode: e.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="E.g. FIRST500"
                  />
                </div>

                <div className="admin-field">
                  <label>Single Use Only</label>
                  <div className="toggle-row">
                    <button
                      type="button"
                      className={`admin-toggle ${
                        form.singleUse ? "admin-toggle-on" : ""
                      }`}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          singleUse: !f.singleUse,
                        }))
                      }
                    >
                      {form.singleUse ? "Yes" : "No"}
                    </button>
                    <small className="muted">
                      If enabled, each customer can use this coupon only once.
                    </small>
                  </div>
                </div>
              </div>

              {/* Row 2: Title */}
              <div className="admin-field">
                <label>
                  Offer Title <span className="req">*</span>
                </label>
                <input
                  className="admin-input-control"
                  value={form.offerTitle}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, offerTitle: e.target.value }))
                  }
                  placeholder="Short, clear title for this offer"
                />
              </div>

              {/* Row 3: Description */}
              <div className="admin-field">
                <label>Offer Description</label>
                <textarea
                  className="admin-input-control"
                  rows={3}
                  value={form.offerDescription}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      offerDescription: e.target.value,
                    }))
                  }
                  placeholder="Explain what this offer does, terms, etc."
                />
              </div>

              {/* Row 4: Discount */}
              <div className="admin-field-grid">
                <div className="admin-field">
                  <label>
                    Discount Value <span className="req">*</span>
                  </label>
                  <input
                    className="admin-input-control mono"
                    inputMode="decimal"
                    value={form.discount}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        discount: e.target.value.replace(/[^\d.]/g, ""),
                      }))
                    }
                    placeholder="e.g. 50"
                  />
                </div>

                <div className="admin-field">
                  <label>Discount Type</label>
                  <select
                    className="admin-input-control"
                    value={form.discountType}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        discountType: e.target.value,
                      }))
                    }
                  >
                    <option value="fixed">Fixed (₹)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                  <small
                    className="muted"
                    style={{ display: "block", marginTop: 4 }}
                  >
                    <BadgeInfo size={11} style={{ marginRight: 4 }} />
                    For percentage, you can also limit discount using &quot;Max
                    Discount&quot;.
                  </small>
                </div>
              </div>

              {/* Row 5: Min / Max */}
              <div className="admin-field-grid">
                <div className="admin-field">
                  <label>Minimum Order Amount</label>
                  <input
                    className="admin-input-control mono"
                    inputMode="decimal"
                    value={form.minimumAmount}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        minimumAmount: e.target.value.replace(/[^\d.]/g, ""),
                      }))
                    }
                    placeholder="e.g. 499"
                  />
                </div>

                <div className="admin-field">
                  <label>Maximum Discount (₹)</label>
                  <input
                    className="admin-input-control mono"
                    inputMode="decimal"
                    value={form.maximumDiscount}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        maximumDiscount: e.target.value.replace(/[^\d.]/g, ""),
                      }))
                    }
                    placeholder={
                      form.discountType === "percentage"
                        ? "Cap for percentage discount"
                        : "Optional"
                    }
                  />
                </div>
              </div>

              <div className="admin-drawer-footer sticky-footer">
                <button
                  type="button"
                  className="admin-btn admin-btn-neutral btn--md"
                  onClick={closeDrawer}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary btn--md"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="admin-spin" size={16} />
                  ) : null}
                  {mode === "add" ? "Create Offer" : "Update Offer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ASSIGN-TO-CUSTOMER DRAWER */}
      {assignOpen && (
        <div className="admin-drawer-backdrop" onClick={closeAssignDrawer}>
          <div
            className="admin-drawer admin-drawer-compact"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-drawer-header">
              <h4 className="admin-drawer-title">Assign Offer to Customer</h4>
              <button className="admin-icon-btn" onClick={closeAssignDrawer}>
                <X size={18} />
              </button>
            </div>

            <form
              className="admin-drawer-body admin-drawer-form admin-drawer-scroll"
              onSubmit={(e) => {
                e.preventDefault();
                if (!assignSubmitting) saveAssign();
              }}
            >
              <div className="admin-field">
                <label>Selected Offer</label>
                <div className="admin-pill-row">
                  <span className="admin-pill">
                    {val(
                      assigningOffer?.couponCode,
                      assigningOffer?.coupon_code,
                      assigningOffer?.tag_code,
                      assigningOffer?.code
                    ) || "—"}
                  </span>
                  <span className="muted small">
                    {val(
                      assigningOffer?.offerTitle,
                      assigningOffer?.offer_title,
                      assigningOffer?.offer_name,
                      assigningOffer?.name
                    ) || ""}
                  </span>
                </div>
              </div>

              <div className="admin-field">
                <label>
                  Customer Mobile <span className="req">*</span>
                </label>
                <input
                  className="admin-input-control mono"
                  inputMode="numeric"
                  maxLength={10}
                  value={assignForm.mobileNumber}
                  onChange={(e) =>
                    setAssignForm((f) => ({
                      ...f,
                      mobileNumber: e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10),
                    }))
                  }
                  placeholder="10-digit mobile number"
                />
                <small className="muted">
                  We will automatically send it as <b>91 + number</b> in the API.
                </small>
              </div>

              <div className="admin-drawer-footer sticky-footer">
                <button
                  type="button"
                  className="admin-btn admin-btn-neutral btn--md"
                  onClick={closeAssignDrawer}
                  disabled={assignSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary btn--md"
                  disabled={assignSubmitting}
                >
                  {assignSubmitting ? (
                    <Loader2 className="admin-spin" size={16} />
                  ) : null}
                  Assign Offer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
  </div>
);

}
